// eslint-disable-next-line no-restricted-imports -- This background query runs in entry points without ReportErrorContextProvider.
import {reportError} from '@github-ui/failbot'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {IssueFieldSelection} from '@github-ui/issue-fields-shared/issue-field'
import {parsePinnedIssueFields} from '@github-ui/issue-fields-shared/issue-field'
import {useQuery} from '@github-ui/react-query'
import {clientSideRelayFetchQueryRetained} from '@github-ui/relay-environment'
import {useEffect, useState} from 'react'
import {useRelayEnvironment} from 'react-relay'
import {graphql, type Subscription} from 'relay-runtime'

import type {
  useIssueFormSecondaryQuery,
  useIssueFormSecondaryQuery$data,
} from './__generated__/useIssueFormSecondaryQuery.graphql'
import {
  fetchIssueFormSecondary,
  issueFormSecondaryQueryKey,
  type IssueFormSecondaryRepository,
} from './fetch-issue-form-secondary'

type useIssueFormSecondaryQueryProps = {
  owner: string
  repo: string
  repoId: string
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const useIssueFormSecondaryGraphqlQuery = graphql`
  query useIssueFormSecondaryQuery($id: ID!) {
    node(id: $id) {
      ... on Repository {
        securityPolicyUrl
        contributingFileUrl
        codeOfConductFileUrl
        supportFileUrl
        owner {
          __typename
          ... on Organization {
            pinnedIssueFields(first: 10) {
              nodes {
                __typename
                ... on IssueFieldText {
                  id
                  name
                  dataType
                  visibility
                }
                ... on IssueFieldSingleSelect {
                  id
                  name
                  dataType
                  visibility
                }
                ... on IssueFieldDate {
                  id
                  name
                  dataType
                  visibility
                }
                ... on IssueFieldNumber {
                  id
                  name
                  dataType
                  visibility
                }
                ... on IssueFieldMultiSelect {
                  id
                  name
                  dataType
                  visibility
                }
              }
            }
            issueFields(first: 100) {
              totalCount
              nodes {
                __typename
                ... on IssueFieldText {
                  id
                  visibility
                }
                ... on IssueFieldSingleSelect {
                  id
                  visibility
                }
                ... on IssueFieldDate {
                  id
                  visibility
                }
                ... on IssueFieldNumber {
                  id
                  visibility
                }
                ... on IssueFieldMultiSelect {
                  id
                  visibility
                }
              }
            }
            issueTypes(first: 1) {
              totalCount
            }
          }
          ... on User {
            issueTypes(first: 1) {
              totalCount
            }
          }
        }
      }
    }
  }
`

export type SecondaryQueryData = {
  securityPolicyUrl: string | null
  contributingFileUrl: string | null
  codeOfConductFileUrl: string | null
  supportFileUrl: string | null
  orgPinnedFields: readonly IssueFieldSelection[] | null
  fieldVisibilityMap: Record<string, string>
  orgHasIssueFields: boolean
  orgHasIssueTypes: boolean
}

const SAFE_DEFAULTS: SecondaryQueryData = {
  securityPolicyUrl: null,
  contributingFileUrl: null,
  codeOfConductFileUrl: null,
  supportFileUrl: null,
  orgPinnedFields: null,
  fieldVisibilityMap: {},
  orgHasIssueFields: true,
  orgHasIssueTypes: true,
}

type OwnerWithIssueTypes = {
  readonly issueTypes?: {
    readonly totalCount: number
  } | null
}

function hasIssueTypes(owner: object): owner is OwnerWithIssueTypes {
  return 'issueTypes' in owner
}

function mapSecondaryQueryData(
  repository: IssueFormSecondaryRepository | NonNullable<useIssueFormSecondaryQuery$data['node']>,
): SecondaryQueryData {
  const owner = 'owner' in repository && repository.owner ? repository.owner : null
  const organization = owner?.__typename === 'Organization' ? owner : null
  const pinnedIssueFieldNodes = organization?.pinnedIssueFields?.nodes
  const orgPinnedFields = parsePinnedIssueFields(
    pinnedIssueFieldNodes?.filter(node => node?.__typename !== '%other') ?? undefined,
  )

  const fieldVisibilityMap: Record<string, string> = {}
  for (const node of organization?.issueFields?.nodes ?? []) {
    if (node && node.__typename !== '%other' && 'id' in node && 'visibility' in node) {
      fieldVisibilityMap[node.id] = node.visibility
    }
  }

  return {
    securityPolicyUrl: repository.securityPolicyUrl ?? null,
    contributingFileUrl: repository.contributingFileUrl ?? null,
    codeOfConductFileUrl: repository.codeOfConductFileUrl ?? null,
    supportFileUrl: repository.supportFileUrl ?? null,
    orgPinnedFields,
    fieldVisibilityMap,
    orgHasIssueFields: (organization?.issueFields?.totalCount ?? 0) > 0,
    orgHasIssueTypes: (owner && hasIssueTypes(owner) ? (owner.issueTypes?.totalCount ?? 0) : 0) > 0,
  }
}

export function useIssueFormSecondaryQuery({
  owner,
  repo,
  repoId,
}: useIssueFormSecondaryQueryProps): SecondaryQueryData | null {
  const isDataRouterEnabled = isFeatureEnabled('issues_new_react_data_router')
  const environment = useRelayEnvironment()
  const [secondaryQueryResult, setSecondaryQueryResult] = useState<{repoId: string; data: SecondaryQueryData} | null>(
    null,
  )
  const serverQuery = useQuery({
    queryKey: issueFormSecondaryQueryKey(owner, repo),
    queryFn: ({signal}) => fetchIssueFormSecondary(owner, repo, signal),
    select: mapSecondaryQueryData,
    enabled: isDataRouterEnabled,
    retry: false,
  })

  useEffect(() => {
    if (isDataRouterEnabled && serverQuery.error) reportError(serverQuery.error)
  }, [isDataRouterEnabled, serverQuery.error])

  useEffect(() => {
    if (isDataRouterEnabled) return

    let rethrowTimeout: ReturnType<typeof setTimeout> | undefined
    const subscription: Subscription = clientSideRelayFetchQueryRetained<useIssueFormSecondaryQuery>({
      environment,
      query: useIssueFormSecondaryGraphqlQuery,
      variables: {
        id: repoId,
      },
    }).subscribe({
      next: (data: useIssueFormSecondaryQuery$data) => {
        setSecondaryQueryResult({
          repoId,
          data: data.node ? mapSecondaryQueryData(data.node) : SAFE_DEFAULTS,
        })
      },
      error: (error: Error) => {
        // Rethrow asynchronously so the failure reaches the global error handler
        // (console + error reporting) instead of being swallowed by this Relay
        // subscription callback, while we still recover with safe defaults below.
        rethrowTimeout = setTimeout(() => {
          throw error
        })
        setSecondaryQueryResult({
          repoId,
          data: {
            securityPolicyUrl: null,
            contributingFileUrl: null,
            codeOfConductFileUrl: null,
            supportFileUrl: null,
            orgPinnedFields: null,
            fieldVisibilityMap: {},
            orgHasIssueFields: true,
            orgHasIssueTypes: true,
          },
        })
      },
    })

    return () => {
      subscription.unsubscribe()
      if (rethrowTimeout) clearTimeout(rethrowTimeout)
    }
  }, [environment, isDataRouterEnabled, repoId])

  if (isDataRouterEnabled) {
    if (serverQuery.isError) return SAFE_DEFAULTS
    return serverQuery.data ?? null
  }

  // Only return the result when it belongs to the current repository. While a new
  // request is in flight after switching repos, this prevents the previous org's
  // type/field flags, pinned fields, and visibility map from leaking into the form.
  return secondaryQueryResult?.repoId === repoId ? secondaryQueryResult.data : null
}
